import {WorkOrderStatusLabel} from '@hconnect/common/components/eventProperties'
import {useWidth} from '@hconnect/common/hooks/useWidth'
import {EventsTab, SortOrder} from '@hconnect/common/types'
import {Column, PaginationOptions} from '@hconnect/uikit/src/lib2'
import {
  AllFilterOptions,
  WorkOrderSortBy,
  workOrderFilterOptions,
  FilterOptions
} from '@widgets/shift-event-filters'
import {TFunction} from 'i18next'
import {isEqual} from 'lodash'
import React, {FC, useState} from 'react'

import {ShiftEventsFilterBar} from '../components/filter'
import {
  generateWorkOrderEquipmentNumberColumn,
  getTitleColumn,
  Label,
  Secondary
} from '../components/tableHelpers'
import {ColumnsMap, WorkOrder} from '../types'

import {EventsListView} from './EventsListView'
import {WorkOrderSideCard} from './WorkOrderSideCard'

const generateColumnsMap = (t: TFunction, freeSearchText?: string): ColumnsMap<WorkOrder> => {
  const workOrderIdColumn: Column<WorkOrder> = {
    key: 'id',
    label: <Label>{t('shiftEvent.label.workOrderId')}</Label>,
    customTemplate: ({id}) => <Secondary>{id}</Secondary>
  }
  const generateStatusColumn = (type?: 'small' | 'regular'): Column<WorkOrder> => ({
    headProps: type === 'small' ? {style: {paddingLeft: '4px'}} : {},
    key: 'status',
    sortable: true,
    width: type === 'small' ? 0 : undefined,
    label: <Label>{t('shiftEvent.label.status')}</Label>,
    customTemplate: (item: WorkOrder) => (
      <Secondary>
        <WorkOrderStatusLabel status={item.status} iconOnly={type === 'small'} />
      </Secondary>
    )
  })

  return {
    XS: [
      workOrderIdColumn,
      getTitleColumn(t, freeSearchText),
      generateWorkOrderEquipmentNumberColumn('small', t, freeSearchText),
      generateStatusColumn('small')
    ],
    S: [
      workOrderIdColumn,
      getTitleColumn(t, freeSearchText),
      generateWorkOrderEquipmentNumberColumn('small', t, freeSearchText),
      generateStatusColumn('small')
    ],
    M: [
      workOrderIdColumn,
      getTitleColumn(t, freeSearchText),
      generateWorkOrderEquipmentNumberColumn('small', t, freeSearchText),
      generateStatusColumn('regular')
    ],
    L: [
      workOrderIdColumn,
      getTitleColumn(t, freeSearchText),
      generateWorkOrderEquipmentNumberColumn('regular', t, freeSearchText),
      generateStatusColumn('regular')
    ],
    XL: [
      workOrderIdColumn,
      getTitleColumn(t, freeSearchText),
      generateWorkOrderEquipmentNumberColumn('regular', t, freeSearchText),
      generateStatusColumn('regular')
    ]
  }
}

type WorkOrdersListViewProps = {
  activeTab: EventsTab
  setActiveTab: (tab: EventsTab) => void
  activeFilter: AllFilterOptions
  events?: WorkOrder[]
  isLoading?: boolean
  showBottomProgress?: boolean
  onFilterChange: (next: FilterOptions) => void
  paginationOptions?: PaginationOptions
  plantId: string
}

export const WorkOrdersListView: FC<WorkOrdersListViewProps> = (props) => {
  const breakPoint = useWidth()
  const isSmall = breakPoint === 'xs' || breakPoint === 'sm'
  const [isMobileFilterOpen, openMobileFilter] = useState(false)

  const onSort = (sortBy: string, sortOrder?: SortOrder) => {
    const next = {
      ...props.activeFilter,
      sortWorkOrderBy: sortBy as WorkOrderSortBy,
      woSortDir: sortOrder
    }
    if (!sortBy || !sortOrder || isEqual(props.activeFilter, next)) {
      return
    }
    props.onFilterChange(next)
  }

  return (
    <EventsListView<WorkOrder, WorkOrderSortBy>
      activeTab={props.activeTab}
      setActiveTab={props.setActiveTab}
      activeFilter={props.activeFilter}
      events={props.events}
      isLoading={props.isLoading}
      paginationOptions={props.paginationOptions}
      plantId={props.plantId}
      urlParamId="workOrderId"
      generateColumnsMap={(t) => generateColumnsMap(t, props.activeFilter.freeText)}
      SideCardComponent={WorkOrderSideCard}
      sortedBy={props.activeFilter.sortWorkOrderBy}
      sortDirection={props.activeFilter.woSortDir}
      onSort={onSort}
      headerActionContent={
        <ShiftEventsFilterBar
          activeSettings={props.activeFilter}
          isCollapsible={isSmall}
          onChange={props.onFilterChange}
          openMobileFilter={openMobileFilter}
          isMobileFilterOpen={isMobileFilterOpen}
          plantId={props.plantId}
          options={workOrderFilterOptions}
          defaultFilters={['timeRange']}
        />
      }
    />
  )
}
